/**
 * NDT field tickets (batch 4, fieldiq-api#1157). Typed BY HAND from the API
 * branch feat/ndt_inspection-b4-api at c10e983 — routes/ndt-inspection-tickets.routes.ts
 * and lib/ndt-field-tickets.ts (shapeTicket / shapeLine). api#1157 was not merged
 * when this was written, so nothing here is generated from the manifest mirror.
 *
 * TWO SURFACES, two casings, by design (API CLAUDE.md "match the surface"):
 * the office reads camelCase (`NdtTicket`), the phone's /field/* reads the same
 * object snake_cased (`NdtFieldTicket`). Writes are snake_case on both.
 *
 * MONEY IS THE API'S. `amount` is quantity × rate computed by the server (a
 * client `amount` is a 400); `money` is its verdict per line; `subtotal` is
 * NULL while any live line has no price. Nothing here holds a computed figure.
 */

export const NDT_TICKET_STATUSES = ['draft', 'signed', 'approved', 'invoiced', 'void'] as const;
export type NdtTicketStatus = (typeof NDT_TICKET_STATUSES)[number];
export const NDT_TICKET_SCOPES = ['job', 'day', 'final'] as const;
export type NdtTicketScope = (typeof NDT_TICKET_SCOPES)[number];
export const NDT_LINE_UNITS = ['per_weld', 'per_shot', 'per_crew_day', 'per_hour', 'per_distance', 'flat', 'consumable'] as const;
export type NdtLineUnit = (typeof NDT_LINE_UNITS)[number];
/** The units a person adds by hand; per_weld / per_shot lines come only from recorded exams. */
export const NDT_MANUAL_LINE_UNITS = ['per_crew_day', 'per_hour', 'per_distance', 'flat', 'consumable'] as const;
export type NdtManualLineUnit = (typeof NDT_MANUAL_LINE_UNITS)[number];
export type NdtLineMoney = 'priced' | 'no_charge' | 'no_rate';

export interface NdtTicketSignature {
  signerName: string | null;
  signerTitle: string | null;
  signedAt: string | null;
  signedBy: string | null;
  hasImage: boolean;
}

export interface NdtTicket {
  id: string;
  ticketNumber: string;
  jobId: string;
  jobNumber: string | null;
  jobStatus: string | null;
  ticketMode: string | null;
  scope: string;
  workDate: string | null;
  status: string;
  currency: string | null;
  subtotal: number | null;
  lineCount: number;
  customerId: string | null;
  customerName: string | null;
  locationName: string | null;
  lsd: string | null;
  provinceState: string | null;
  lineRef: string | null;
  projectRef: string | null;
  acceptanceCode: string | null;
  country: string | null;
  poNumber: string | null;
  afeNumber: string | null;
  costCode: string | null;
  notes: string | null;
  signature: NdtTicketSignature | null;
  approvedBy: string | null;
  approvedAt: string | null;
  /** Who approved it (api#1166); null before approval, or when that user has no name. */
  approvedByName?: string | null;
  voidReason: string | null;
  voidedBy: string | null;
  voidedAt: string | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NdtTicketLine {
  id: string;
  ticketId: string;
  source: string;
  unit: string;
  description: string;
  rateId: string | null;
  rateCode: string | null;
  rateName: string | null;
  method: string | null;
  distanceUnit: string | null;
  examId: string | null;
  weldId: string | null;
  weldNumber: string | null;
  workDate: string | null;
  quantity: number | null;
  rate: number | null;
  amount: number | null;
  money: NdtLineMoney | string;
  rateSource: string | null;
  unpricedReason: string | null;
  sortOrder: number | null;
  deletedAt: string | null;
  deletedBy: string | null;
  deleteReason: string | null;
  voidedAt: string | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface NdtTicketDetail extends NdtTicket {
  lines: NdtTicketLine[];
  removedLines: NdtTicketLine[];
}

// ─── The field surface: the same objects, snake_cased ────────────────────────

export interface NdtFieldTicketLine {
  id: string;
  ticket_id: string;
  source: string;
  unit: string;
  description: string;
  rate_id: string | null;
  rate_code: string | null;
  rate_name: string | null;
  method: string | null;
  distance_unit: string | null;
  exam_id: string | null;
  weld_number: string | null;
  work_date: string | null;
  quantity: number | null;
  rate: number | null;
  amount: number | null;
  money: NdtLineMoney | string;
  rate_source: string | null;
  unpriced_reason: string | null;
}

export interface NdtFieldTicket {
  id: string;
  ticket_number: string;
  job_id: string;
  job_number: string | null;
  job_status: string | null;
  ticket_mode: string | null;
  scope: string;
  work_date: string | null;
  status: string;
  currency: string | null;
  subtotal: number | null;
  line_count: number;
  customer_id: string | null;
  customer_name: string | null;
  location_name: string | null;
  lsd: string | null;
  po_number: string | null;
  afe_number: string | null;
  cost_code: string | null;
  signature: { signer_name: string | null; signer_title: string | null; signed_at: string | null; has_image: boolean } | null;
  approved_at: string | null;
  void_reason: string | null;
  created_at: string | null;
}

export interface NdtFieldTicketDetail extends NdtFieldTicket {
  lines: NdtFieldTicketLine[];
  removed_lines: NdtFieldTicketLine[];
}

// ─── Writes (snake_case) ─────────────────────────────────────────────────────

/**
 * A manual line. EITHER `rate_id` (a rate-card line — its unit and price) OR,
 * for the office only, `unit` + `description` + `rate`. Never `amount`.
 */
export interface NdtLineCreate {
  quantity: number;
  rate_id?: string;
  unit?: NdtManualLineUnit;
  description?: string;
  rate?: number | null;
  work_date?: string;
}

/** quantity (anyone on the ticket), description (manual line), rate / rate_id (the office). */
export interface NdtLineUpdate {
  quantity?: number;
  description?: string;
  rate?: number | null;
  rate_id?: string;
}

export interface NdtTicketSign {
  signer_name: string;
  signer_title?: string | null;
  po_number?: string | null;
  afe_number?: string | null;
  cost_code?: string | null;
  signature_data: string;
}

export interface NdtTicketHeaderUpdate {
  po_number?: string | null;
  afe_number?: string | null;
  cost_code?: string | null;
  notes?: string | null;
}

/** GET /ndt-inspection/rates (0529, live) — the rate card, read-only here. */
export interface NdtRate {
  id: string;
  code: string;
  name: string;
  description: string | null;
  unit: string;
  unitLabel: string | null;
  rate: number;
  currency: string | null;
  distanceUnit: string | null;
  method: string | null;
  methodLabel: string | null;
  customerId: string | null;
  customerName: string | null;
  scope: 'customer' | 'default' | string;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  inForce: boolean;
  notes: string | null;
}
